<template>
  <div class="dictionary">
    <a-card :bordered="false">
      <!-- 标题 -->
      <template #title>
        <span style="margin-left: 10px">{{ $t('Dictionaries.DictionaryMainLeft.dictionaryManagement') }}</span>
        <div class="head-btn">
          <a-button style="margin-right: 8px"
                    data-cy="cy-button-refresh"
                    @click="fetchDictionary">
            <redo-outlined :rotate="270" />{{ $t('Dictionaries.DictionaryMainLeft.refresh') }}
          </a-button>
          <a-button @click="create"
                    data-cy="cy-button-create">
            <plus-outlined />{{ $t('Dictionaries.DictionaryMainLeft.createDictionary') }}
          </a-button>
        </div>
      </template>
      <!-- 表格 -->
      <div class="table">
        <a-scroll-table class="scroll"
                        data-cy="cy-table"
                        :columns="DICTIONARY_COLUMNS"
                        :data-source="dataSource"
                        :pagination="pagination"
                        @change="handleTableChange"
                        rowKey="id"
                        :customRow="customRow"
                        :row-selection="{
                          selectedRowKeys: selectedRow,
                          onChange: selectedRowKeys,
                          type: 'radio',
                        }">
          <template #bodyCell="{ column, record }">
            <template v-if="column.dataIndex === 'operation'">
              <a @click="editRow(record)"
                 data-cy="cy-button-edit">{{ $t('Dictionaries.DictionaryMainLeft.edit') }}</a>
              <a @click="deleteRow(record)"
                 data-cy="cy-button-delete"
                 style="margin-left:10px">{{ $t('Dictionaries.DictionaryMainLeft.delete') }}</a>
            </template>
          </template>
        </a-scroll-table>
      </div>
    </a-card>
    <a-modal v-model:visible="visible"
             :title="title"
             @ok="onCancelOk"
             :confirm-loading="confirmLoading"
             @cancel="onCancel">
      <a-form :model="addFormState"
              name="basic"
              data-cy="cy-modal-form"
              ref="aFrom"
              :label-col="{ span: 6 }"
              autocomplete="off">
        <a-form-item :label="$t('Dictionaries.DictionaryMainLeft.dictionaryKey')"
                     name="key"
                     :rules="[{ required: true, message: $t('Dictionaries.DictionaryMainLeft.enterDictionaryKey') }]">
          <a-input v-model:value="addFormState.key"
                   data-cy="cy-input-key"
                   :placeholder="$t('Dictionaries.DictionaryMainLeft.dictionaryKey')" />
        </a-form-item>
        <a-form-item :label="$t('Dictionaries.DictionaryMainLeft.dictionaryName')"
                     name="name"
                     :rules="[{ required: true, message: $t('Dictionaries.DictionaryMainLeft.enterDictionaryName') }]">
          <a-input v-model:value="addFormState.name"
                   data-cy="cy-input-name"
                   :placeholder="$t('Dictionaries.DictionaryMainLeft.dictionaryName')" />
        </a-form-item>
      </a-form>
    </a-modal>
    <!-- 删除弹出框 -->
    <a-modal v-model:visible="delVisible"
             :title="$t('Dictionaries.DictionaryMainLeft.deleteDictionaryTitle')"
             @ok="onDelModalOk"
             :confirm-loading="loading"
             @cancel="delVisible = false">
      <p style="font-size:16px">{{ $t('Dictionaries.DictionaryMainLeft.deleteDictionaryConfirm') }}{{ addFormState.name }}
      </p>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { AScrollTable } from '@iota-cn/ui-components'
import { onMounted, ref, reactive } from 'vue'
import type { FormInstance } from 'ant-design-vue/es/form/Form'
import { dictionaryList, dictionaryAdd, dictionaryEdit, dictionaryDelete } from '../service/DictionaryService'
import { DICTIONARY_COLUMNS, type DictionaryForm } from './index'
import type { DictionarySingle } from "../service/type/DictionaryType"
import { dictionaryStore } from '../DictionaryPlugin'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const store = dictionaryStore()

const confirmLoading = ref<boolean>(false)
const loading = ref<boolean>(false)
const addFormState = reactive<DictionaryForm>({
  key: '',
  name: ''
})
const dataSource = ref<DictionaryForm[]>([])
onMounted(() => {
  fetchDictionary()
})
const fetchDictionary = async () => {
  const res = await dictionaryList()
  dataSource.value = res.dictionaries
}
const visible = ref<boolean>(false)
const aFrom = ref<FormInstance | null>()
const create = () => {
  visible.value = true
  title.value = t('Dictionaries.DictionaryMainLeft.newDictionary')
  visibleType.value = 1
}
const title = ref<string>('')
const visibleType = ref<number>()
// 分页
const pagination = ref({
  total: 0,
  pageSize: 20,
  showSizeChanger: true,
  pageSizeOptions: ['20', '40', '60', '80'], // 每页中显示的数据
  showTotal: (total: number) => t('Dictionaries.DictionaryMainLeft.totalItems', { total }), // 分页中显示总的数据
  size: 'small',
})
const handleTableChange = (event: any) => {
  pagination.value.pageSize = event.pageSize
}
const onCancelOk = () => {
  aFrom.value?.validate().then(async (res: any) => {
    if (res.outOfDate) {
      return
    }
    confirmLoading.value = true
    if (visibleType.value == 1) {
      await dictionaryAdd(addFormState)
    } else {
      editData.value.key = addFormState.key
      editData.value.name = addFormState.name
      await dictionaryEdit(editData.value)
    }
    confirmLoading.value = false
    onCancel()
    await fetchDictionary()
  }).catch()
}
const onCancel = () => {
  aFrom.value?.resetFields()
  visible.value = false
  addFormState.key = ''
  addFormState.name = ''
}
const editData = ref<DictionarySingle>({
  belongTo: '',
  createBy: '',
  createdAt: '',
  id: '',
  isBuiltIn: false,
  key: '',
  name: '',
  tenant: '',
  updatedAt: ''
})
const editRow = (record: any) => {
  visible.value = true
  title.value = t('Dictionaries.DictionaryMainLeft.editDictionary')
  addFormState.key = record.key
  addFormState.name = record.name
  editData.value = record
  visibleType.value = 2
}
const delVisible = ref<boolean>(false)
const deleteData = ref<DictionarySingle>({
  belongTo: '',
  createBy: '',
  createdAt: '',
  id: '',
  isBuiltIn: false,
  key: '',
  name: '',
  tenant: '',
  updatedAt: ''
})
const deleteRow = (record: any) => {
  delVisible.value = true
  addFormState.name = record.name
  deleteData.value = record
}
const onDelModalOk = async () => {
  loading.value = true
  await dictionaryDelete(deleteData.value)
  loading.value = false
  delVisible.value = false
  await fetchDictionary()
}
const selectedRow = ref<any>([])
const customRow = (record: DictionarySingle) =>
({
  onClick: () => {
    selectedRow.value = selectedRow.value.splice(1, 1)
    selectedRow.value.push(record.id)
    store.dictionarySingleId = record.id
    store.dictionarySingleKey = record.key
  },
})
const selectedRowKeys = (record: any, selectedData: any) => {
  selectedRow.value = record
  store.dictionarySingleId = record[0]
}
</script>

<style lang="less" scoped>
.dictionary {
  .scroll {
    height: calc(100vh - 330px);
  }

  .head-btn {
    float: right;
  }
}
</style>
